import React from "react";
import { toProfileHeroAvatarUrl } from "../../utils/sessionUser";
import { UserAvatar, type UserAvatarProps } from "./UserAvatar";

type ProfileAvatarProps = {
  src: string;
  alt?: string;
  variant: "hero" | "chip";
  className?: string;
  xpWeekCrown?: UserAvatarProps["xpWeekCrown"];
  avatarFrameUrl?: string | null;
  avatarFrameSmallUrl?: string | null;
  displayName?: string | null;
  avatarColor?: string | null;
  firstLetter?: string | null;
};

export type ProfileAvatarHeroWithFallbackProps = {
  /** After `toProfileHeroAvatarUrl` — empty when no photo. */
  resolvedSrc: string;
  /** True when the account had a real image URL before high-res upgrade (show &lt;img&gt; first). */
  rawHadPhoto: boolean;
  displayName?: string | null;
  avatarColor?: string | null;
  firstLetter?: string | null;
  className?: string;
  /** Synthetic / leaderboard bot: same letter tile colours + typography as in-play bot avatars, hero-sized. */
  syntheticBotLetterTile?: boolean;
  xpWeekCrown?: UserAvatarProps["xpWeekCrown"];
  avatarFrameUrl?: string | null;
  avatarFrameSmallUrl?: string | null;
};

/**
 * Profile modal header: photo when present, otherwise same letter tile as trader leaderboard rows.
 */
export function ProfileAvatarHeroWithFallback({
  resolvedSrc,
  rawHadPhoto,
  displayName,
  avatarColor,
  firstLetter,
  className = "",
  syntheticBotLetterTile = false,
  xpWeekCrown = null,
  avatarFrameUrl = null,
  avatarFrameSmallUrl = null,
}: ProfileAvatarHeroWithFallbackProps) {
  return (
    <UserAvatar
      src={rawHadPhoto ? resolvedSrc : ""}
      displayName={displayName}
      avatarColor={avatarColor}
      firstLetter={firstLetter}
      syntheticBotLetterTile={syntheticBotLetterTile}
      xpWeekCrown={xpWeekCrown}
      avatarFrameUrl={avatarFrameUrl}
      avatarFrameSmallUrl={avatarFrameSmallUrl}
      size="hero"
      className={className}
      loading="eager"
    />
  );
}

/** Profile header (large) or friends strip / activity — letter tile when no photo. */
export function ProfileAvatar({
  src,
  alt = "",
  variant,
  className = "",
  xpWeekCrown = null,
  avatarFrameUrl = null,
  avatarFrameSmallUrl = null,
  displayName,
  avatarColor,
  firstLetter,
}: ProfileAvatarProps) {
  const resolvedSrc = variant === "hero" ? toProfileHeroAvatarUrl(src) : src;
  return (
    <UserAvatar
      src={resolvedSrc}
      displayName={displayName}
      avatarColor={avatarColor}
      firstLetter={firstLetter}
      xpWeekCrown={xpWeekCrown}
      avatarFrameUrl={avatarFrameUrl}
      avatarFrameSmallUrl={avatarFrameSmallUrl}
      size={variant === "hero" ? "hero" : "chip"}
      className={className}
      alt={alt}
      loading={variant === "hero" ? "eager" : "lazy"}
    />
  );
}

export { UserAvatar } from "./UserAvatar";
